import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  FIREFLY_DEFAULT_MOTION_SPEED_MULTIPLIER,
  clampFireflyMotionSpeedMultiplier,
  parseFireflyMotionSpeedMultiplier,
} from "@/lib/firefly/fireflyMotionSpeed";
import {
  fetchFireflyMotionSpeedMultiplier,
  FIREFLY_MOTION_SPEED_CHANGED_EVENT,
  saveFireflyMotionSpeedMultiplier,
  type FireflyMotionSpeedSaveResult,
} from "@/lib/firefly/fireflyMotionSpeedClient";

type FireflyMotionSpeedContextValue = {
  /** Effective speed for this browser session (preview or saved). */
  multiplier: number;
  /** Last value persisted on the server. */
  savedMultiplier: number;
  isDirty: boolean;
  loading: boolean;
  source: "platform" | "default";
  canEdit: boolean;
  /** Local preview only — does not persist. */
  setPreviewMultiplier: (value: number) => void;
  resetPreview: () => void;
  saveMultiplier: (value?: number) => Promise<FireflyMotionSpeedSaveResult>;
  refresh: () => Promise<void>;
};

const FireflyMotionSpeedContext =
  createContext<FireflyMotionSpeedContextValue | null>(null);

export function FireflyMotionSpeedProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [savedMultiplier, setSavedMultiplier] = useState(
    FIREFLY_DEFAULT_MOTION_SPEED_MULTIPLIER,
  );
  const [previewMultiplier, setPreviewMultiplierState] = useState<
    number | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<"platform" | "default">("default");

  const multiplier = previewMultiplier ?? savedMultiplier;
  const isDirty =
    previewMultiplier !== null && previewMultiplier !== savedMultiplier;

  const canEdit =
    auth.status === "ready" && auth.realHasRole("platform_owner");

  const refresh = useCallback(async () => {
    if (auth.status !== "ready" || !auth.session?.access_token) {
      setSavedMultiplier(FIREFLY_DEFAULT_MOTION_SPEED_MULTIPLIER);
      setPreviewMultiplierState(null);
      setSource("default");
      setLoading(false);
      return;
    }
    setLoading(true);
    const res = await fetchFireflyMotionSpeedMultiplier();
    if (res) {
      setSavedMultiplier(
        parseFireflyMotionSpeedMultiplier(res.motion_speed_multiplier),
      );
      setSource(res.source);
    } else {
      setSavedMultiplier(FIREFLY_DEFAULT_MOTION_SPEED_MULTIPLIER);
      setSource("default");
    }
    setLoading(false);
  }, [auth.status, auth.session?.access_token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onChanged = () => void refresh();
    window.addEventListener(FIREFLY_MOTION_SPEED_CHANGED_EVENT, onChanged);
    return () =>
      window.removeEventListener(FIREFLY_MOTION_SPEED_CHANGED_EVENT, onChanged);
  }, [refresh]);

  const setPreviewMultiplier = useCallback(
    (value: number) => {
      if (!canEdit) return;
      const next = clampFireflyMotionSpeedMultiplier(value);
      setPreviewMultiplierState(next === savedMultiplier ? null : next);
    },
    [canEdit, savedMultiplier],
  );

  const resetPreview = useCallback(() => {
    setPreviewMultiplierState(null);
  }, []);

  const saveMultiplier = useCallback(
    async (value?: number): Promise<FireflyMotionSpeedSaveResult> => {
      const next = clampFireflyMotionSpeedMultiplier(
        value ?? previewMultiplier ?? savedMultiplier,
      );
      if (!canEdit) {
        return { ok: false, error: "platform_owner required." };
      }
      const res = await saveFireflyMotionSpeedMultiplier(next);
      if (!res.ok) {
        return res;
      }
      setSavedMultiplier(next);
      setPreviewMultiplierState(null);
      setSource("platform");
      return { ok: true };
    },
    [canEdit, previewMultiplier, savedMultiplier],
  );

  const value = useMemo<FireflyMotionSpeedContextValue>(
    () => ({
      multiplier,
      savedMultiplier,
      isDirty,
      loading,
      source,
      canEdit,
      setPreviewMultiplier,
      resetPreview,
      saveMultiplier,
      refresh,
    }),
    [
      multiplier,
      savedMultiplier,
      isDirty,
      loading,
      source,
      canEdit,
      setPreviewMultiplier,
      resetPreview,
      saveMultiplier,
      refresh,
    ],
  );

  return (
    <FireflyMotionSpeedContext.Provider value={value}>
      {children}
    </FireflyMotionSpeedContext.Provider>
  );
}

export function useFireflyMotionSpeedContext(): FireflyMotionSpeedContextValue {
  const ctx = useContext(FireflyMotionSpeedContext);
  if (!ctx) {
    throw new Error(
      "useFireflyMotionSpeedContext must be used within FireflyMotionSpeedProvider",
    );
  }
  return ctx;
}
